import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle, RotateCcw, XCircle, MousePointer2, HelpCircle, PlayCircle } from 'lucide-react';
import { useGameSounds } from '../../../core/hooks/useGameSounds';
import TacticalOverlay from '../../../components/shared/TacticalOverlay';
import ConfidenceSlider from '../../../components/shared/ConfidenceSlider';

export default function ResumeRoaster({ gameData, onComplete }) {
  const { playError, playWin, playExtinguish, playSuccess } = useGameSounds();
  
  const { 
    variants, 
    id,
    preGameBriefing,
    failureLogic,
    baseTotalErrors = 3
  } = gameData;
  
  // --- STATE ---
  const [stage, setStage] = useState('briefing'); 
  const [confidencePre, setConfidencePre] = useState(5);
  
  // Game Logic
  const [activeVariant, setActiveVariant] = useState(null); 
  const [tapsLeft, setTapsLeft] = useState(0);
  const [foundErrors, setFoundErrors] = useState([]);
  
  const [activeFeedback, setActiveFeedback] = useState(null);
  const [activeIntervention, setActiveIntervention] = useState(null);

  const [firstReactionTime, setFirstReactionTime] = useState(null);
  const [startTime, setStartTime] = useState(null);

  // --- 1. DIFFICULTY CALCULATOR (Untimed) ---
  const calculateDifficulty = (conf) => {
      if (conf >= 8) return { taps: baseTotalErrors + 1, label: "EXPERT (1 Miss Allowed)" };
      if (conf >= 5) return { taps: baseTotalErrors + 3, label: "STANDARD (3 Misses Allowed)" };
      return { taps: baseTotalErrors + 5, label: "TRAINING (5 Misses Allowed)" };
  };

  // --- 2. INITIALIZATION ---
  useEffect(() => {
    if (preGameBriefing) {
        setStage('briefing');
    } else {
        setStage('pre');
    }
    const randomVariant = variants[Math.floor(Math.random() * variants.length)];
    setActiveVariant(randomVariant);
  }, [preGameBriefing, variants]);

  // --- 3. START GAME ---
  const handleStartGame = () => {
      const settings = calculateDifficulty(confidencePre);
      setTapsLeft(settings.taps);
      setFoundErrors([]);
      setStartTime(Date.now());
      setStage('playing');
  };

  const handleRetry = () => {
    setFoundErrors([]);
    setActiveFeedback(null);
    setActiveIntervention(null);
    
    const randomVariant = variants[Math.floor(Math.random() * variants.length)];
    setActiveVariant(randomVariant);

    const settings = calculateDifficulty(confidencePre);
    setTapsLeft(settings.taps);
    setStartTime(Date.now());
    
    setStage('playing');
  };

  // --- 4. AUTO-DISMISS FEEDBACK ---
  useEffect(() => {
    if (activeFeedback) {
      const timer = setTimeout(() => {
        setActiveFeedback(null);
      }, 3000); 
      return () => clearTimeout(timer);
    }
  }, [activeFeedback]);

  const triggerFailure = (reasonCode) => {
      setStage('intervention');
      playExtinguish();

      const logic = failureLogic?.[reasonCode] || {
          title: "Critical Error Missed",
          script: "You ran out of mistake budget. In the enterprise, missing these details ruins deals and reputations.",
          videoUrl: null
      };

      setActiveIntervention(logic);
  };

  // --- 5. INTERACTION HANDLER (Inline Spans) ---
  const handleSegmentClick = (segment) => {
    if (stage !== 'playing' || foundErrors.includes(segment.id)) return;

    if (segment.isError) {
      // --- CORRECT HIT ---
      playSuccess();
      if (foundErrors.length === 0) setFirstReactionTime(Date.now() - startTime);

      const newFound = [...foundErrors, segment.id];
      setFoundErrors(newFound);
      setActiveFeedback({ type: 'success', msg: segment.feedback });

      // WIN CONDITION
      if (newFound.length === baseTotalErrors) {
        setStage('post'); 
        setTimeout(() => playWin(), 500);
      }
    } else {
      // --- WRONG HIT (STRIKE) ---
      playError();
      const newTaps = tapsLeft - 1;
      setTapsLeft(newTaps);
      
      if (newTaps <= 0) {
          triggerFailure('out_of_taps');
      } else {
          setActiveFeedback({ type: 'error', msg: `False Positive! That looks correct. ${newTaps} taps left.` });
      }
    }
  };

  const handleFinalLog = () => {
    const payload = {
      moduleId: id,
      confidencePre,   
      confidencePost: confidencePre, // Bypass post-slider for speed 
      reactionTime: firstReactionTime, 
      score: foundErrors.length === baseTotalErrors ? 100 : 50,
      timestamp: Date.now()
    };
    onComplete(payload);
  };

  const tapsColor = tapsLeft <= 1 ? 'text-red-600 font-bold animate-pulse' : 'text-gray-600';

  return (
    <div className="h-full flex flex-col bg-slate-50 rounded-xl overflow-hidden border border-gray-200 relative min-h-[500px]">
      
      {/* OVERLAYS */}
      <AnimatePresence>
        {stage === 'briefing' && preGameBriefing && (
          <div className="absolute inset-0 z-[100]">
            <TacticalOverlay type="briefing" data={preGameBriefing} onContinue={() => setStage(startTime ? 'playing' : 'pre')} />
          </div>
        )}
        {stage === 'intervention' && activeIntervention && (
          <div className="absolute inset-0 z-[100]">
            <TacticalOverlay type="intervention" data={activeIntervention} onRetry={handleRetry} />
          </div>
        )}
      </AnimatePresence>

      {/* PRE-GAME */}
      {stage === 'pre' && (
          <motion.div key="pre" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-grow flex items-center justify-center p-6 bg-white z-50 absolute inset-0">
            <div className="max-w-md w-full">
              <ConfidenceSlider value={confidencePre} onChange={setConfidencePre} title="Baseline Assessment" subtitle="How sharp is your eye for Outbound Communication errors?" />
              <div className="mt-6 p-4 bg-gray-50 rounded-xl text-center border border-gray-200">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Difficulty: <span className="text-blue-600">{calculateDifficulty(confidencePre).label}</span></p>
                  <div className="flex justify-center gap-4 text-sm font-medium text-gray-700">
                      <span className="flex items-center gap-1"><MousePointer2 size={16}/> {calculateDifficulty(confidencePre).taps} Max Taps</span>
                  </div>
              </div>
              <button onClick={handleStartGame} className="mt-6 w-full bg-blue-600 text-white font-black py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition"><PlayCircle /> START THE AUDIT</button>
            </div>
          </motion.div>
      )}

      {/* HEADER */}
      {(stage === 'playing' || stage === 'post') && (
          <div className="bg-white p-4 border-b flex justify-between items-center shadow-sm z-10">
            <div className="flex items-center gap-3">
                <button 
                    onClick={() => setStage('briefing')} 
                    className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-500 transition-colors"
                    title="Review Mission Briefing"
                >
                    <HelpCircle size={16} />
                </button>
                <div className="hidden sm:flex flex-col">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Mistakes Found</span>
                    <span className="text-sm font-bold text-gray-800">{foundErrors.length}/{baseTotalErrors}</span>
                </div>
            </div>
            <div className="flex items-center gap-4">
              <div className={`flex items-center text-sm font-bold ${tapsColor}`}><MousePointer2 className="w-4 h-4 mr-1.5" /><span>{tapsLeft} Taps Left</span></div>
            </div>
          </div>
      )}

      {/* GAME AREA */}
      {stage === 'playing' && (
          <div className="flex-grow p-6 overflow-y-auto bg-gray-100 flex flex-col items-center">
            
            <div className="w-full max-w-2xl bg-white shadow-xl rounded-xl border border-gray-200 overflow-hidden mb-8">
                <div className="bg-slate-800 p-4 text-white">
                    <h3 className="text-xs font-mono font-bold uppercase tracking-widest text-slate-400 mb-1">Context:</h3>
                    <p className="text-sm font-medium">{activeVariant?.context}</p>
                </div>
                
                <div className="p-8 text-base text-gray-800 font-sans leading-relaxed whitespace-pre-wrap">
                    {activeVariant?.documentText?.map((segment) => (
                      <span
                        key={segment.id}
                        onClick={() => handleSegmentClick(segment)}
                        className={`cursor-pointer transition-all px-1 rounded-md ${
                          foundErrors.includes(segment.id) 
                            ? 'bg-red-100 text-red-700 border-b-2 border-red-500 font-bold decoration-clone' 
                            : 'hover:bg-yellow-100'
                        }`}
                      >
                        {segment.text}{' '}
                      </span>
                    ))}
                </div>
            </div>
          </div>
      )}

      {/* FEEDBACK POPUP */}
      <AnimatePresence>
        {activeFeedback && (
          <motion.div initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -50, opacity: 0 }} className="absolute top-20 left-4 right-4 z-50">
            <div className={`${activeFeedback.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'} text-white p-4 rounded-xl shadow-lg flex items-start gap-3 cursor-pointer`} onClick={() => setActiveFeedback(null)}>
                <AlertCircle className="w-6 h-6 flex-shrink-0" />
                <div><h4 className="font-bold text-white/80 text-xs uppercase tracking-wider">{activeFeedback.type === 'success' ? 'Caught' : 'Warning'}</h4><p className="font-medium text-sm md:text-base">{activeFeedback.msg}</p></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SUCCESS OVERLAY */}
      {stage === 'post' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 bg-white/95 z-50 flex flex-col items-center justify-center p-8 text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mb-4" />
          <h2 className="text-3xl font-black text-gray-900 mb-2 uppercase">Audit Successful!</h2>
          <p className="text-gray-600 mb-8 max-w-md font-medium">You spotted the critical outbound errors. Attention to detail prevents enterprise disasters.</p>
          <button onClick={handleFinalLog} className="bg-blue-600 text-white font-bold py-4 px-10 rounded-xl shadow-lg hover:bg-blue-700 transition transform hover:scale-105">Finish Lesson</button>
        </motion.div>
      )}
    </div>
  );
}